import React, { useState } from 'react';
import { Modal, Button, Typography, Space, Alert } from 'antd';
import { SyncOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface MembershipValidationModalProps {
    visible: boolean;
    onValidate: () => Promise<void>;
    onCancel: () => void;
    errorMessage?: string | null;
    isInactiveMember?: boolean;
    // Completion-eligible (Inactive / Pending / Cancelled): a blocking prompt to
    // review & complete details, wired to the self-service edit/save below it.
    // Not dismissible into the rest of the app — the only action continues to the
    // profile form.
    completionRequired?: boolean;
    onCompleteDetails?: () => void;
}

const MembershipValidationModal: React.FC<MembershipValidationModalProps> = ({
    visible,
    onValidate,
    onCancel,
    errorMessage,
    isInactiveMember = false,
    completionRequired = false,
    onCompleteDetails
}) => {
    const [isValidating, setIsValidating] = useState(false);

    const handleValidate = async () => {
        setIsValidating(true);
        try {
            await onValidate();
        } catch (error) {
            // Error is already handled in the context
            console.error('Validation error:', error);
        } finally {
            setIsValidating(false);
        }
    };

    // Blocking completion prompt takes precedence over the "contact the club"
    // dead-end: a completion-eligible member is told to finish their details.
    if (completionRequired) {
        return (
            <Modal
                title={
                    <Space>
                        <SyncOutlined />
                        <span>Membership awaiting activation</span>
                    </Space>
                }
                open={visible}
                closable={false}
                maskClosable={false}
                keyboard={false}
                footer={[
                    <Button key="complete" type="primary" onClick={onCompleteDetails}>
                        Review my details
                    </Button>
                ]}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        message="Your membership isn't active yet — it's awaiting activation by the club. Please review your details and update anything that's missing or out of date."
                        type="info"
                        showIcon
                    />
                </Space>
            </Modal>
        );
    }

    if (isInactiveMember) {
        return (
            <Modal
                title={
                    <Space>
                        <SyncOutlined />
                        <span>Account Not Active</span>
                    </Space>
                }
                open={visible}
                closable={false}
                maskClosable={false}
                footer={[
                    <Button key="ok" type="primary" onClick={onCancel}>
                        OK
                    </Button>
                ]}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        message="Your account is not currently active. Please contact the club to activate your account."
                        type="warning"
                        showIcon
                    />
                </Space>
            </Modal>
        );
    }

    return (
        <Modal
            title={
                <Space>
                    <SyncOutlined />
                    <span>Membership Validation Required</span>
                </Space>
            }
            open={visible}
            closable={false}
            maskClosable={false}
            footer={[
                <Button key="cancel" onClick={onCancel} disabled={isValidating}>
                    Later
                </Button>,
                <Button
                    key="validate"
                    type="primary"
                    loading={isValidating}
                    onClick={handleValidate}
                >
                    Validate Now
                </Button>
            ]}
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {errorMessage ? (
                    <Alert
                        message={errorMessage}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                ) : (
                    <>
                        <Text>
                            We need to sync your membership details with your account.
                            This is a one-time process that ensures your membership is properly linked.
                        </Text>
                        <Text type="secondary">
                            Click "Validate Now" to complete this process, or "Later" to skip for now.
                        </Text>
                    </>
                )}
            </Space>
        </Modal>
    );
};

export default MembershipValidationModal;
